import { useField } from '@framework/form/field/useField.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { Alert } from 'antd';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTreeReferencesContext } from '../../../../../tree/references/TreeReferencesContext.tsx';
import { Datatype } from '../../../../expressions/datatype/Datatype.ts';
import { type ISwitchExpressionCase } from '../../../../expressions/repository/switch-expression/ISwitchExpression.ts';
import { SwitchExpressionInput } from '../../../../expressions/repository/switch-expression/input/SwitchExpressionInput.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { LayoutHideConfigInput } from '../../common/config/LayoutHideConfigInput.tsx';
import { LayoutRemoveBorderConfigInput } from '../../common/config/LayoutRemoveBorderConfigInput.tsx';
import { CodeLabelInputForm } from '../../inputs/common/CodeLabelInputForm.tsx';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps.ts';
import { type ISwitchWidget } from './ISwitchWidget.ts';
import { SwitchWidgetType } from './SwitchWidget.type.tsx';

export interface IScoreWidgetConfigComponentProps
  extends IWidgetComponentProps<ISwitchWidget> {}

export function SwitchWidgetConfigComponent(
  props: IScoreWidgetConfigComponentProps,
) {
  const { widget } = props;
  const options = getWidgetProps(props);
  const referencesContext = useTreeReferencesContext();

  const selectedDatatype = useWatch({ name: 'datatype' });

  const references = useMemo(() => {
    selectedDatatype; // NOSONAR
    return referencesContext.references.filter(r => r.id !== widget?.id);
  }, [referencesContext.references, selectedDatatype, widget?.id]);

  const widgetFieldDatatype = useMemo(() => {
    return SwitchWidgetType.getReference?.([], {
      ...widget,
      datatype: selectedDatatype,
    })?.field;
  }, [selectedDatatype, widget]);

  const cases = useField<ISwitchExpressionCase[]>('expression.cases');

  if (!widgetFieldDatatype) {
    return (
      <Alert
        type="error"
        message="Errore"
        description="Non è stato possibile recuperare il tipo di dato dall'elemento. Si prega di contattare l'assistenza."
      />
    );
  }

  return (
    <>
      <FormItem name="datatype" label="Tipo di dato">
        <SelectInput
          name="datatype"
          options={[
            { label: 'Testo', value: Datatype.String },
            { label: 'Numero', value: Datatype.Number },
            { label: 'Data', value: Datatype.Date },
            { label: 'Booleano', value: Datatype.Boolean },
          ]}
          // Quando cambio il tipo, resetto tutti i value impostati a null
          onChange={(value, _, onChange) => {
            if (value === selectedDatatype) return;

            const caseList = cases.getValue();
            if (!caseList) {
              onChange(value);
              return;
            }

            const newCases = caseList.map(c => ({
              ...c,
              value: null as any,
            }));

            cases.setValue(newCases);
            onChange(value);
          }}
        />
      </FormItem>
      {!selectedDatatype && (
        <Alert
          type="warning"
          message="Seleziona un tipo di dato"
          description="È necessario selezionare un tipo di dato per poter procedere alla configurazione completa."
          style={{ marginBottom: 16, marginTop: 8 }}
          showIcon
        />
      )}
      {selectedDatatype && (
        <>
          <CodeLabelInputForm widgetOptions={options} />

          <SwitchExpressionInput
            name={'expression'}
            fieldRef={widgetFieldDatatype}
            references={references}
          />

          <RowElement>
            <LayoutHideConfigInput />
            <LayoutRemoveBorderConfigInput />
          </RowElement>
        </>
      )}
    </>
  );
}
